import { useState } from 'react';
import { Share2, Check, Copy, MessageCircle } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger
} from './ui/dropdown-menu';
import { toast } from 'sonner';

// Inline icons for social platforms (kept minimal — avoids extra deps)
const FacebookIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M22 12.06C22 6.5 17.52 2 12 2S2 6.5 2 12.06c0 5.02 3.66 9.18 8.44 9.94v-7.03H7.9v-2.91h2.54V9.85c0-2.51 1.49-3.9 3.77-3.9 1.09 0 2.24.2 2.24.2v2.47h-1.26c-1.24 0-1.63.77-1.63 1.56v1.87h2.78l-.44 2.91h-2.33V22c4.78-.76 8.44-4.92 8.44-9.94Z" />
  </svg>
);

const XIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24h-6.66l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25h6.831l4.713 6.231 5.446-6.231Zm-1.161 17.52h1.833L7.084 4.126H5.117l11.966 15.644Z" />
  </svg>
);

const WhatsappIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.472-.148-.67.15-.199.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.762-1.653-2.06-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.15-.174.2-.298.298-.497.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.372-.01-.571-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.064 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.71.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347Zm-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.375a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.885-9.885 9.885ZM20.52 3.449C18.24 1.245 15.24 0 12.045 0 5.463 0 .104 5.334.101 11.891c0 2.096.549 4.142 1.595 5.945L0 24l6.335-1.652a11.882 11.882 0 0 0 5.71 1.447h.006c6.585 0 11.946-5.336 11.949-11.893a11.821 11.821 0 0 0-3.48-8.463Z" />
  </svg>
);

/**
 * ShareButton — dropdown with WhatsApp / X / Facebook / Copy link.
 * @param url      Absolute URL to share (defaults to current href)
 * @param title    Short title used as share text
 * @param text     Optional longer description
 * @param variant  Button visual variant: 'icon' | 'compact' | 'full'
 */
export const ShareButton = ({
  url,
  title = 'Check this out on Eat From Local',
  text = '',
  variant = 'compact',
  testIdSuffix = ''
}) => {
  const [copied, setCopied] = useState(false);
  const shareUrl = url || (typeof window !== 'undefined' ? window.location.href : '');
  const encodedUrl = encodeURIComponent(shareUrl);
  const encodedTitle = encodeURIComponent(title);
  const encodedText = encodeURIComponent(text ? `${title} — ${text}` : title);

  const links = {
    whatsapp: `https://wa.me/?text=${encodedText}%20${encodedUrl}`,
    x: `https://x.com/intent/tweet?text=${encodedTitle}&url=${encodedUrl}`,
    facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`
  };

  const handleCopy = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(shareUrl);
      } else {
        // Fallback
        const ta = document.createElement('textarea');
        ta.value = shareUrl;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      toast.success('Link copied to clipboard');
      setTimeout(() => setCopied(false), 1800);
    } catch {
      toast.error('Could not copy — long-press the URL to copy manually');
    }
  };

  const triggerButton = variant === 'icon' ? (
    <Button
      size="icon"
      variant="outline"
      className="rounded-full"
      style={{ borderColor: '#E5E0D8', backgroundColor: '#FFFFFF' }}
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      data-testid={`share-btn${testIdSuffix ? '-' + testIdSuffix : ''}`}
      aria-label="Share"
    >
      <Share2 size={16} strokeWidth={1.5} />
    </Button>
  ) : variant === 'full' ? (
    <Button
      variant="outline"
      className="rounded-md"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      style={{ borderColor: '#E5E0D8', fontFamily: 'Manrope', fontWeight: 500, fontSize: '14px' }}
      data-testid={`share-btn${testIdSuffix ? '-' + testIdSuffix : ''}`}
    >
      <Share2 size={16} strokeWidth={1.5} className="mr-1.5" />
      Share
    </Button>
  ) : (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); e.stopPropagation(); }}
      className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs hover:bg-white/90 border border-[#E5E0D8] backdrop-blur transition-colors"
      style={{ backgroundColor: 'rgba(255,255,255,0.85)', color: '#1A1A1A', fontFamily: 'Manrope', fontWeight: 500 }}
      data-testid={`share-btn${testIdSuffix ? '-' + testIdSuffix : ''}`}
      aria-label="Share this dish"
    >
      <Share2 size={12} strokeWidth={1.75} />
      Share
    </button>
  );

  const socialItem = (label, href, icon, testId) => (
    <DropdownMenuItem
      onClick={(e) => e.stopPropagation()}
      asChild
      data-testid={testId}
    >
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        className="flex items-center gap-2 cursor-pointer"
        style={{ fontFamily: 'Manrope', fontSize: '14px' }}
      >
        {icon}
        <span>{label}</span>
      </a>
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>{triggerButton}</DropdownMenuTrigger>
      <DropdownMenuContent
        align="end"
        className="w-52"
        onClick={(e) => e.stopPropagation()}
        data-testid={`share-menu${testIdSuffix ? '-' + testIdSuffix : ''}`}
      >
        <DropdownMenuLabel style={{ fontFamily: 'Manrope', fontSize: '11px', letterSpacing: '0.1em', color: '#5C5C5C' }} className="uppercase">
          Share this dish
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {socialItem('WhatsApp', links.whatsapp, <span style={{ color: '#25D366' }}><WhatsappIcon size={16} /></span>, 'share-whatsapp')}
        {socialItem('X (Twitter)', links.x, <span style={{ color: '#1A1A1A' }}><XIcon size={16} /></span>, 'share-x')}
        {socialItem('Facebook', links.facebook, <span style={{ color: '#1877F2' }}><FacebookIcon size={16} /></span>, 'share-facebook')}
        <DropdownMenuSeparator />
        <DropdownMenuItem
          onClick={handleCopy}
          className="flex items-center gap-2 cursor-pointer"
          style={{ fontFamily: 'Manrope', fontSize: '14px' }}
          data-testid="share-copy"
        >
          {copied ? <Check size={16} className="text-green-600" /> : <Copy size={16} />}
          {copied ? 'Link copied!' : 'Copy link'}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default ShareButton;
